/**
 * ID Generation Utility
 * 
 * Provides consistent, collision-resistant ID generation using nanoid.
 * This replaces the previous Math.random() approach for better reliability.
 * 
 * @example
 * ```ts
 * import { generateId } from './utils/idGenerator';
 * 
 * const newId = generateId(); // Returns a unique ID like "V1StGXR8_Z5jdHi6B-myT"
 * ```
 */

import { nanoid, customAlphabet } from 'nanoid';

/**
 * Generates a unique, URL-safe ID
 * 
 * Uses nanoid for collision-resistant ID generation.
 * Default length is 9 characters, which provides good balance between
 * uniqueness and brevity for most use cases.
 * 
 * @param length - Optional length of the ID (default: 9)
 * @returns A unique, URL-safe string ID
 */
export const generateId = (length: number = 9): string => {
  return nanoid(length);
};

/**
 * Generates an ID with a custom alphabet
 * 
 * Useful when you need IDs that match specific requirements
 * (e.g., only uppercase letters and numbers).
 * 
 * @param alphabet - Custom alphabet string
 * @param length - Length of the ID
 * @returns A unique ID using the custom alphabet
 * 
 * @example
 * ```ts
 * const numericId = generateCustomId('0123456789', 6); // "123456"
 * ```
 */
export const generateCustomId = (alphabet: string, length: number = 9): string => {
  const generate = customAlphabet(alphabet, length);
  return generate();
};
